import { Component, OnInit, HostListener, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import {
  AssetService,
  AssetData,
  Layout,
  LayoutPreset,
  Cell,
  CameraCell,
  AdCell,
  MultiviewLinkCell,
  ValidationIssue
} from '../../../../../services/asset/asset.service';
import { ProductConfigurationService } from '../../../../../services/product_configuration/product_configuration.service';
import { awsPath } from '../../../../../configurations/configuration';


declare var PubNub: any;
const VALID_ACTIONS = ['start', 'stream', 'weather', 'technical', 'stop', 'clear'];
type ActionT = typeof VALID_ACTIONS[number];

interface CellRef { layoutId: string; cellIndex: number; }
interface CellHitChoice { index: number; badge: string; label: string; }
interface CellHitChooser { layoutId: string; x: number; y: number; choices: CellHitChoice[]; }

@Component({
  selector: 'app-asset-config-v2',
  templateUrl: './add.component.html',
  styleUrls: ['./add.component.scss']
})
export class AssetConfigV2Component implements OnInit, OnDestroy {

  // Schema-version switcher (owned by the host; rendered beside the page title).
  @Input() schemaVersion = 2;
  @Output() schemaVersionChange = new EventEmitter<number>();

  onSchemaVersionChange(v: number | string) {
    this.schemaVersionChange.emit(Number(v) === 2 ? 2 : 1);
  }

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  asset: AssetData;       // canonical v2 — bound directly by the layout cards
  tmpAsset: AssetData;    // scratch copy for Asset Edit popup (Cancel-safe)
  assetPath: string = '';

  // Stats
  totalLayouts = 0;
  enabledLayouts = 0;
  configuredStreams = 0;
  lastUpdated = '--';

  // Live overlay (PubNub) state — unchanged from v1
  isLive = false;
  currentOverlay: 'stream' | 'technical' | 'weather' | null = null;
  isStarting = false;
  isStopping = false;
  pubnub: any = null;
  pubnubReady = false;
  pubnubListenerRef: any = null;
  activeAction: ActionT = 'start';
  lastTokenProcessed = '';
  lastAppliedAction = '';
  lastAppliedAt = 0;

  // ---------------------------------------------------------------------------
  // Cell-config popup state
  // ---------------------------------------------------------------------------

  showCellPopup = false;
  currentCellRef: CellRef | null = null;
  cellHitChooser: CellHitChooser | null = null;
  form_cellType: 'camera' | 'ad' | 'multiviewLink' = 'camera';
  form_x = '';
  form_y = '';
  form_width = '';
  form_height = '';

  // camera
  form_camIndex: number | null = null;

  // ad
  form_adProvider: 'image' | 'adsense' | 'gam' = 'image';
  form_imageUrl = '';
  form_adImageUploading = false;
  form_redirectUrl = '';
  form_alt = '';
  form_adClient = '';
  form_adSlot = '';
  form_adUnitPath = '';
  form_adSizes = '[[970,250],[728,90]]';
  form_adDivId = '';

  // multiview link
  form_targetLayoutId: string | null = null;
  form_linkDisplayName = '';

  // ---------------------------------------------------------------------------
  // Add-layout popup state
  // ---------------------------------------------------------------------------

  showAddLayoutPopup = false;
  form_newLayoutName = '';
  form_newLayoutMasterUrl = '';
  form_newLayoutPreset: LayoutPreset = 'custom';

  // ---------------------------------------------------------------------------
  // Asset edit popup
  // ---------------------------------------------------------------------------

  showAssetEditPopup = false;

  // ---------------------------------------------------------------------------
  // Tabs + JSON view + Graph view + Validation
  // ---------------------------------------------------------------------------

  activeTab: 'visual' | 'json' | 'graph' = 'visual';
  jsonMode: 'raw' | 'tree' = 'tree';

  // Raw JSON editor
  rawJsonText = '';
  rawJsonError: string | null = null;
  rawJsonDirty = false;

  // Tree editor expanded paths
  expandedTreePaths: Set<string> = new Set(['', 'layouts', 'streamUrls']);

  // Validation panel collapsed/expanded
  showValidationPanel = true;

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  constructor(
    private svc: AssetService,
    private activatedRoute: ActivatedRoute,
    private toastrService: ToastrService,
    private productConfigurationService: ProductConfigurationService
  ) {
    // initialise with a default v2 asset so the template can bind safely
    this.asset = this.svc.defaultAssetV2('');
    this.tmpAsset = this.cloneAsset(this.asset);
  }

  get isLiveContent(): boolean {
    return !!this.asset && this.asset.contentType === 'live';
  }

  ngOnInit() {
    this.activatedRoute.params.subscribe((params: Params) => {
      this.assetPath = params['id'];
      this.loadAsset();
    });
  }

  ngOnDestroy() {
    this.svc.clearLayoutAndAsset();
  }

  // ---------------------------------------------------------------------------
  // Load / persist
  // ---------------------------------------------------------------------------

  private loadAsset() {
    this.svc.getConfig(this.assetPath).subscribe(
      (response: any) => {
        let raw: any = null;
        if (response && response.data && response.data.asset_id && response.data.config_data) {
          try { raw = JSON.parse(response.data.config_data); } catch (_) { raw = null; }
        }
        this.asset = this.svc.adaptV1ToV2(raw, this.assetPath);
        if (!this.asset.id) this.asset.id = this.assetPath;
        this.tmpAsset = this.cloneAsset(this.asset);
        this.updateStats();
        this.maybeInitPubNub();
      },
      (err: any) => {
        console.error('getConfig failed', err);
        this.asset = this.svc.defaultAssetV2(this.assetPath);
        this.tmpAsset = this.cloneAsset(this.asset);
        this.updateStats();
      }
    );
  }

  private cloneAsset(a: AssetData): AssetData {
    return JSON.parse(JSON.stringify(a));
  }

  // ---------------------------------------------------------------------------
  // Layout CRUD
  // ---------------------------------------------------------------------------

  trackById(_: number, layout: Layout): string {
    return layout.id;
  }

  trackByIndex(i: number): number {
    return i;
  }

  get layouts(): Layout[] {
    return (this.asset && this.asset.layouts) ? this.asset.layouts : [];
  }

  findLayout(id: string): Layout | undefined {
    return this.layouts.find(l => l.id === id);
  }

  setLayoutEnabled(layout: Layout, enabled: boolean) {
    layout.enabled = !!enabled;
    this.updateStats();
  }

  openAddLayoutPopup() {
    this.form_newLayoutName = '';
    this.form_newLayoutMasterUrl = '';
    this.form_newLayoutPreset = 'custom';
    this.showAddLayoutPopup = true;
  }

  closeAddLayoutPopup() {
    this.showAddLayoutPopup = false;
  }

  submitAddLayout() {
    const name = (this.form_newLayoutName || '').trim();
    if (!name) {
      this.toastrService.warning('Layout name is required');
      return;
    }
    const preset = this.form_newLayoutPreset;
    const { rows, cols } = this.presetDimensions(preset);
    const layout: Layout = {
      id: this.svc.makeId('lay'),
      name,
      masterUrl: (this.form_newLayoutMasterUrl || '').trim(),
      preset,
      rows,
      cols,
      enabled: true,
      cells: this.svc.generateCellsForPreset(preset)
    };
    if (!this.asset.layouts) this.asset.layouts = [];
    this.asset.layouts.push(layout);
    this.showAddLayoutPopup = false;
    this.updateStats();
  }

  deleteLayout(layoutId: string) {
    if (!this.asset.layouts) return;
    const links = this.findLinksToLayout(layoutId);
    let msg = 'Delete this layout?';
    if (links.length) {
      const names = Array.from(new Set(links.map(l => l.layout.name))).join(', ');
      msg = `Delete this layout?\n\n${links.length} cell(s) in [${names}] link to it. Those links will be removed.`;
    }
    if (!confirm(msg)) return;
    this.removeLinksToLayout(layoutId);
    this.asset.layouts = this.asset.layouts.filter(l => l.id !== layoutId);
    if (this.asset.defaultLayoutId === layoutId) this.asset.defaultLayoutId = undefined;
    this.updateStats();
  }

  duplicateLayout(layoutId: string) {
    if (!this.asset.layouts) this.asset.layouts = [];
    const layout = this.findLayout(layoutId);
    if (!layout) return;
    const clone: Layout = JSON.parse(JSON.stringify(layout));
    clone.id = this.svc.makeId('lay');
    clone.name = `${layout.name || 'Layout'} Copy`;
    clone.enabled = layout.enabled !== false;
    clone.cells = Array.isArray(clone.cells) ? clone.cells : [];
    const idx = this.asset.layouts.findIndex(l => l.id === layoutId);
    this.asset.layouts.splice(idx >= 0 ? idx + 1 : this.asset.layouts.length, 0, clone);
    this.updateStats();
    this.toastrService.success('Layout duplicated');
  }

  setLanding(layoutId: string) {
    // Radio behaviour: clicking the same one clears it
    if (this.asset.defaultLayoutId === layoutId) {
      this.asset.defaultLayoutId = undefined;
    } else {
      this.asset.defaultLayoutId = layoutId;
    }
  }

  setLayoutPreset(layoutId: string, newPreset: LayoutPreset) {
    const layout = this.findLayout(layoutId);
    if (!layout) return;
    if (layout.preset === newPreset) return;
    if (layout.cells.length > 0 && newPreset !== 'custom') {
      const ok = confirm(`Changing preset will replace existing ${layout.cells.length} cell(s) with new defaults. Continue?`);
      if (!ok) return;
    }
    layout.preset = newPreset;
    const dims = this.presetDimensions(newPreset);
    layout.rows = dims.rows;
    layout.cols = dims.cols;
    layout.cells = this.svc.generateCellsForPreset(newPreset);
    this.updateStats();
  }

  private presetDimensions(p: LayoutPreset): { rows: number; cols: number } {
    if (p === 'grid9') return { rows: 3, cols: 3 };
    if (p === 'grid4') return { rows: 2, cols: 2 };
    if (p === 'grid2') return { rows: 2, cols: 2 };
    if (p === 'gridH') return { rows: 4, cols: 3 };
    return { rows: 2, cols: 2 };
  }

  isLandingLayout(layoutId: string): boolean {
    return !!this.asset && this.asset.defaultLayoutId === layoutId;
  }

  findLinksToLayout(layoutId: string): Array<{ layout: Layout; cellIndex: number }> {
    const out: Array<{ layout: Layout; cellIndex: number }> = [];
    for (const l of this.layouts) {
      for (let i = 0; i < l.cells.length; i++) {
        const c = l.cells[i] as Cell;
        if (c && c.type === 'multiviewLink' && (c as MultiviewLinkCell).targetLayoutId === layoutId) {
          out.push({ layout: l, cellIndex: i });
        }
      }
    }
    return out;
  }

  private removeLinksToLayout(layoutId: string) {
    for (const l of this.layouts) {
      l.cells = l.cells.filter(c => !(c.type === 'multiviewLink' && (c as MultiviewLinkCell).targetLayoutId === layoutId));
    }
  }

  // ---------------------------------------------------------------------------
  // Cell CRUD
  // ---------------------------------------------------------------------------

  openCellFromPreview(layoutId: string, event: MouseEvent) {
    const preview = event.currentTarget as HTMLElement;
    if (!preview) return;

    const hitChoices: CellHitChoice[] = [];
    const cells = Array.prototype.slice.call(preview.querySelectorAll('.cell-preview')) as HTMLElement[];

    for (const el of cells) {
      const rect = el.getBoundingClientRect();
      if (
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom
      ) {
        const idx = parseInt(el.getAttribute('data-cell-index') || '', 10);
        const layout = this.findLayout(layoutId);
        const cell = layout && layout.cells ? layout.cells[idx] : null;
        if (!isNaN(idx) && cell) {
          hitChoices.push({
            index: idx,
            badge: this.cellTypeBadge(cell),
            label: this.cellLabel(cell)
          });
        }
      }
    }

    if (hitChoices.length === 0) {
      this.cellHitChooser = null;
      return;
    }

    hitChoices.sort((a, b) => b.index - a.index);

    if (hitChoices.length === 1) {
      this.openCellConfig(layoutId, hitChoices[0].index);
      return;
    }

    const previewRect = preview.getBoundingClientRect();
    const menuWidth = 220;
    const menuHeight = Math.min(180, 40 + hitChoices.length * 34);
    this.cellHitChooser = {
      layoutId,
      x: this.clampNumber(event.clientX - previewRect.left, 8, Math.max(8, previewRect.width - menuWidth - 8)),
      y: this.clampNumber(event.clientY - previewRect.top, 8, Math.max(8, previewRect.height - menuHeight - 8)),
      choices: hitChoices
    };
  }

  choosePreviewCell(layoutId: string, cellIndex: number) {
    this.cellHitChooser = null;
    this.openCellConfig(layoutId, cellIndex);
  }

  openCellConfig(layoutId: string, cellIndex: number) {
    const layout = this.findLayout(layoutId);
    if (!layout) return;
    this.cellHitChooser = null;
    this.currentCellRef = { layoutId, cellIndex };
    const cell = layout.cells[cellIndex];
    this.resetCellForm();
    if (cell) {
      this.form_cellType = cell.type;
      this.form_x = cell.x;
      this.form_y = cell.y;
      this.form_width = cell.width;
      this.form_height = cell.height;
      if (cell.type === 'camera') {
        const cc = cell as CameraCell;
        this.form_camIndex = cc.camIndex;
      } else if (cell.type === 'ad') {
        const ac = cell as AdCell;
        this.form_adProvider = ac.adProvider || 'image';
        this.form_imageUrl = ac.imageUrl || '';
        this.form_redirectUrl = ac.redirectUrl || '';
        this.form_alt = ac.alt || '';
        this.form_adClient = ac.adClient || '';
        this.form_adSlot = ac.adSlot || '';
        this.form_adUnitPath = ac.adUnitPath || '';
        this.form_adSizes = ac.adSizes ? JSON.stringify(ac.adSizes) : '[[970,250],[728,90]]';
        this.form_adDivId = ac.adDivId || '';
      } else if (cell.type === 'multiviewLink') {
        const lc = cell as MultiviewLinkCell;
        this.form_targetLayoutId = lc.targetLayoutId;
        this.form_linkDisplayName = lc.displayName || '';
      }
    } else {
      // new cell defaults
      this.form_cellType = 'camera';
      this.form_x = '0%';
      this.form_y = '0%';
      this.form_width = '25%';
      this.form_height = '25%';
    }
    this.showCellPopup = true;
  }

  saveCellConfig() {
    if (!this.currentCellRef) return;
    const layout = this.findLayout(this.currentCellRef.layoutId);
    if (!layout) return;

    const x = this.form_x || '0%';
    const y = this.form_y || '0%';
    const width = this.form_width || '25%';
    const height = this.form_height || '25%';

    let newCell: Cell;
    if (this.form_cellType === 'camera') {
      if (this.form_camIndex == null || this.form_camIndex < 0) {
        this.toastrService.warning('Select a camera stream');
        return;
      }
      if (this.form_camIndex >= (this.asset.streamUrls || []).length) {
        this.toastrService.warning('Camera index out of range — add more streams in Edit Streams Config first.');
        return;
      }
      newCell = {
        type: 'camera',
        camIndex: this.form_camIndex,
        x, y, width, height
      };
    } else if (this.form_cellType === 'ad') {
      const cell: AdCell = {
        type: 'ad',
        adProvider: this.form_adProvider,
        x, y, width, height
      };
      if (this.form_adProvider === 'image') {
        if (!this.form_imageUrl || !this.form_redirectUrl) {
          this.toastrService.warning('Image URL and Redirect URL are required');
          return;
        }
        cell.imageUrl = this.form_imageUrl;
        cell.redirectUrl = this.form_redirectUrl;
        cell.alt = this.form_alt;
      } else if (this.form_adProvider === 'adsense') {
        if (!this.form_adClient || !this.form_adSlot) {
          this.toastrService.warning('Ad Client and Ad Slot are required for AdSense');
          return;
        }
        cell.adClient = this.form_adClient;
        cell.adSlot = this.form_adSlot;
      } else if (this.form_adProvider === 'gam') {
        if (!this.form_adUnitPath || !this.form_adDivId) {
          this.toastrService.warning('Ad Unit Path and Div ID are required for GAM');
          return;
        }
        cell.adUnitPath = this.form_adUnitPath;
        cell.adDivId = this.form_adDivId;
        try { cell.adSizes = JSON.parse(this.form_adSizes); } catch (_) { cell.adSizes = [[970, 250]]; }
      }
      newCell = cell;
    } else { // multiviewLink
      if (!this.form_targetLayoutId) {
        this.toastrService.warning('Select a target layout');
        return;
      }
      if (this.form_targetLayoutId === this.currentCellRef.layoutId) {
        this.toastrService.warning('A layout cannot link to itself');
        return;
      }
      const lc: MultiviewLinkCell = {
        type: 'multiviewLink',
        targetLayoutId: this.form_targetLayoutId,
        x, y, width, height,
        displayName: this.form_linkDisplayName || undefined
      };
      newCell = lc;
    }

    if (this.currentCellRef.cellIndex < layout.cells.length) {
      layout.cells[this.currentCellRef.cellIndex] = newCell;
    } else {
      layout.cells.push(newCell);
    }
    this.closeCellConfig();
    this.updateStats();
  }

  closeCellConfig() {
    this.showCellPopup = false;
    this.currentCellRef = null;
    this.resetCellForm();
  }

  duplicateCellFromPopup() {
    if (!this.currentCellRef) return;
    const ref = { layoutId: this.currentCellRef.layoutId, cellIndex: this.currentCellRef.cellIndex };
    // Persist any in-progress form edits first; this closes the popup.
    this.saveCellConfig();
    if (this.showCellPopup) return; // save failed validation
    this.duplicateCell(ref.layoutId, ref.cellIndex);
  }

  duplicateCell(layoutId: string, cellIndex: number) {
    const layout = this.findLayout(layoutId);
    if (!layout || !layout.cells[cellIndex]) return;
    const cloned: Cell = JSON.parse(JSON.stringify(layout.cells[cellIndex]));
    cloned.x = this.shiftPercent(cloned.x, 5);
    cloned.y = this.shiftPercent(cloned.y, 5);
    layout.cells.splice(cellIndex + 1, 0, cloned);
    this.updateStats();
  }

  deleteCellFromPopup() {
    if (!this.currentCellRef) return;
    const ref = this.currentCellRef;
    this.closeCellConfig();
    this.deleteCell(ref.layoutId, ref.cellIndex);
  }

  deleteCell(layoutId: string, cellIndex: number) {
    const layout = this.findLayout(layoutId);
    if (!layout) return;
    if (!confirm('Delete this cell?')) return;
    layout.cells.splice(cellIndex, 1);
    this.updateStats();
  }

  addCell(layoutId: string) {
    const layout = this.findLayout(layoutId);
    if (!layout) return;
    layout.cells.push({
      type: 'camera',
      camIndex: 0,
      x: '0%', y: '0%', width: '25%', height: '25%'
    });
    // open popup for the new cell to encourage configuring it
    this.openCellConfig(layoutId, layout.cells.length - 1);
  }

  private shiftPercent(value: string, delta: number): string {
    const m = (value || '').match(/^(-?\d+(?:\.\d+)?)(.*)$/);
    if (!m) return value;
    const n = parseFloat(m[1]) + delta;
    const unit = m[2] || '%';
    return `${Math.max(0, Math.min(100, n))}${unit}`;
  }

  private clampNumber(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
  }

  private resetCellForm() {
    this.form_cellType = 'camera';
    this.form_x = '';
    this.form_y = '';
    this.form_width = '';
    this.form_height = '';
    this.form_camIndex = null;
    this.form_adProvider = 'image';
    this.form_imageUrl = '';
    this.form_adImageUploading = false;
    this.form_redirectUrl = '';
    this.form_alt = '';
    this.form_adClient = '';
    this.form_adSlot = '';
    this.form_adUnitPath = '';
    this.form_adSizes = '[[970,250],[728,90]]';
    this.form_adDivId = '';
    this.form_targetLayoutId = null;
    this.form_linkDisplayName = '';
  }

  /** Returns the layouts that can be link targets from the current cell-config popup. */
  otherLayouts(): Layout[] {
    const me = this.currentCellRef ? this.currentCellRef.layoutId : null;
    return this.layouts.filter(l => l.id !== me);
  }

  // ---------------------------------------------------------------------------
  // Audio Tracks (named, reusable)
  // ---------------------------------------------------------------------------

  get audioTracks() {
    return (this.asset && this.asset.audioTracks) ? this.asset.audioTracks : [];
  }

  get tmpAudioTracks() {
    if (!this.tmpAsset.audioTracks) this.tmpAsset.audioTracks = [];
    return this.tmpAsset.audioTracks;
  }

  addAudioTrack() {
    if (!this.tmpAsset.audioTracks) this.tmpAsset.audioTracks = [];
    this.tmpAsset.audioTracks.push({
      id: this.svc.makeId('aud'),
      name: `Audio Track ${this.tmpAsset.audioTracks.length + 1}`,
      url: ''
    });
  }

  removeAudioTrack(index: number) {
    if (!this.tmpAsset.audioTracks) return;
    const track = this.tmpAsset.audioTracks[index];
    if (!track) return;
    if (!confirm(`Remove audio track "${track.name}"? Layouts and cameras using it will fall back to the global default.`)) return;
    // unlink references in layouts and streams (tmp + live)
    for (const l of (this.tmpAsset.layouts || [])) {
      if (l.audioTrackId === track.id) l.audioTrackId = undefined;
    }
    for (const s of (this.tmpAsset.streamUrls || [])) {
      if (s.audioTrackId === track.id) s.audioTrackId = undefined;
    }
    this.tmpAsset.audioTracks.splice(index, 1);
  }

  // ---------------------------------------------------------------------------
  // Stream CRUD (unlimited)
  // ---------------------------------------------------------------------------

  addStream() {
    if (!this.tmpAsset.streamUrls) this.tmpAsset.streamUrls = [];
    this.tmpAsset.streamUrls.push({
      name: `Camera ${this.tmpAsset.streamUrls.length + 1}`,
      url: ''
    });
  }

  removeStream(index: number) {
    if (!this.tmpAsset.streamUrls) return;
    if (!confirm(`Remove stream #${index + 1}? Cells referencing this index will become invalid.`)) return;
    this.tmpAsset.streamUrls.splice(index, 1);
  }

  // ---------------------------------------------------------------------------
  // Labels / display
  // ---------------------------------------------------------------------------

  cellLabel(cell: Cell): string {
    if (!cell) return '';
    if (cell.type === 'camera') {
      const cc = cell as CameraCell;
      const stream = (this.asset.streamUrls || [])[cc.camIndex];
      if (!stream) return `Cam ${cc.camIndex + 1} (missing)`;
      return stream.name || `Cam ${cc.camIndex + 1}`;
    }
    if (cell.type === 'ad') {
      const ac = cell as AdCell;
      return `Ad: ${ac.adProvider}`;
    }
    if (cell.type === 'multiviewLink') {
      const lc = cell as MultiviewLinkCell;
      const target = this.findLayout(lc.targetLayoutId);
      return `→ ${target ? target.name : '?'}`;
    }
    return '';
  }

  cellTypeBadge(cell: Cell): string {
    if (!cell) return '';
    if (cell.type === 'camera') return 'CAM';
    if (cell.type === 'ad') return 'AD';
    if (cell.type === 'multiviewLink') return 'LINK';
    return '';
  }

  // ---------------------------------------------------------------------------
  // Stats
  // ---------------------------------------------------------------------------

  updateStats() {
    if (!this.asset) {
      this.totalLayouts = 0;
      this.enabledLayouts = 0;
      this.configuredStreams = 0;
      this.lastUpdated = '--';
      return;
    }
    const layouts = this.asset.layouts || [];
    this.totalLayouts = layouts.length;
    this.enabledLayouts = layouts.filter(l => l.enabled).length;
    let cellCount = 0;
    for (const l of layouts) cellCount += l.cells.length;
    this.configuredStreams = cellCount;
    this.lastUpdated = new Date().toLocaleTimeString();
  }

  // ---------------------------------------------------------------------------
  // Asset edit popup
  // ---------------------------------------------------------------------------

  openeditAssetPopup() {
    this.tmpAsset = this.cloneAsset(this.asset);
    this.showAssetEditPopup = true;
  }

  closeAssetEditPopup() {
    this.showAssetEditPopup = false;
  }

  saveAssetInfo() {
    if (this.tmpAsset.contentType === 'live') {
      if (!this.tmpAsset.signalChannel || !this.tmpAsset.signalChannel.trim()) {
        alert('Signal channel is required for LIVE content.');
        return;
      }
    }
    if (this.tmpAsset.chatEnabled) {
      if (!this.tmpAsset.chatChannel || !this.tmpAsset.chatChannel.trim()) {
        alert('Chat channel is required when Chat is enabled.');
        return;
      }
    }
    if (this.tmpAsset.adEnabled) {
      if (!this.tmpAsset.adTagUrl || !this.tmpAsset.adTagUrl.trim()) {
        alert('Ad tag URL (VAST/VMAP) is required when Ads are enabled.');
        return;
      }
    }

    // Copy editable globals back to this.asset (preserve layouts / defaultLayoutId)
    this.asset.audiourl = this.tmpAsset.audiourl;
    this.asset.programcutUrl = this.tmpAsset.programcutUrl;
    this.asset.Program = this.tmpAsset.Program;
    this.asset.streamUrls = (this.tmpAsset.streamUrls || []).slice();
    this.asset.contentType = this.tmpAsset.contentType;
    this.asset.signalChannel = this.tmpAsset.signalChannel;
    this.asset.chatEnabled = this.tmpAsset.chatEnabled;
    this.asset.chatChannel = this.tmpAsset.chatChannel;
    this.asset.chatEnablePrivate = !!this.tmpAsset.chatEnablePrivate;
    this.asset.chatRoomPrefix = this.tmpAsset.chatRoomPrefix;
    this.asset.adEnabled = !!this.tmpAsset.adEnabled;
    this.asset.adTagUrl = this.tmpAsset.adTagUrl;
    this.asset.adTriggerMode = this.tmpAsset.adTriggerMode;
    this.asset.multiViewEnabled = this.tmpAsset.multiViewEnabled;
    this.asset.audioTracks = (this.tmpAsset.audioTracks || []).slice();

    this.maybeInitPubNub();
    this.closeAssetEditPopup();
    this.updateStats();
  }

  // ---------------------------------------------------------------------------
  // Backend / export / reset
  // ---------------------------------------------------------------------------

  generateBackendJSON() {
    return this.svc.generateBackendJSONV2(this.asset);
  }

  exportConfig() {
    const data = this.generateBackendJSON();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = (window.URL || (window as any).webkitURL).createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stream-config-${this.assetPath || 'export'}.json`;
    a.click();
    setTimeout(() => { (window.URL || (window as any).webkitURL).revokeObjectURL(url); }, 0);
  }

  sendToBackend() {
    if (!this.canSave) {
      this.toastrService.warning(`Cannot save: ${this.errorCount} error(s), ${this.warningCount} warning(s). Fix issues first.`);
      this.showValidationPanel = true;
      return;
    }
    const config: any = this.generateBackendJSON();
    config.asset_id = this.assetPath;
    this.svc.sendConfig(config).subscribe(
      () => {
        this.toastrService.success('Configuration saved successfully!');
        this.loadAsset();
      },
      (err: any) => {
        console.error(err);
        this.toastrService.warning('Failed to send configuration.');
      }
    );
  }

  resetAll() {
    if (!confirm('Reset all configurations to defaults?')) return;
    this.svc.clearLayoutAndAsset();
    this.asset = this.svc.defaultAssetV2(this.assetPath);
    this.tmpAsset = this.cloneAsset(this.asset);
    this.updateStats();
    this.toastrService.success('All configurations reset!');
  }

  remove() {
    if (!confirm('Remove all configurations?')) return;
    this.svc.clearLayoutAndAsset();
    this.asset = this.svc.defaultAssetV2(this.assetPath);
    // Empty out layouts entirely so the editor starts from a blank slate
    this.asset.layouts = [];
    this.asset.defaultLayoutId = undefined;
    this.tmpAsset = this.cloneAsset(this.asset);
    this.updateStats();
    this.toastrService.success('All configurations removed!');
  }

  // ---------------------------------------------------------------------------
  // Ad image upload helper (unchanged from v1)
  // ---------------------------------------------------------------------------

  onAdImageSelected(event: any) {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    this.applyAdImageDimensions(file);
    this.form_adImageUploading = true;
    this.productConfigurationService.uploadFile(file, awsPath.asset).then((res: any) => {
      if (res && res.Location) {
        this.form_imageUrl = res.Location;
      } else {
        this.toastrService.warning('Image upload failed');
      }
      this.form_adImageUploading = false;
    }).catch(() => {
      this.toastrService.warning('Image upload failed');
      this.form_adImageUploading = false;
    });
  }

  private applyAdImageDimensions(file: File) {
    if (!file || !file.type || file.type.indexOf('image/') !== 0) return;
    const objectUrl = (window.URL || (window as any).webkitURL).createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      if (this.form_cellType === 'ad' && this.form_adProvider === 'image') {
        this.form_width = `${img.naturalWidth}px`;
        this.form_height = `${img.naturalHeight}px`;
      }
      (window.URL || (window as any).webkitURL).revokeObjectURL(objectUrl);
    };
    img.onerror = () => {
      (window.URL || (window as any).webkitURL).revokeObjectURL(objectUrl);
    };
    img.src = objectUrl;
  }

  // ---------------------------------------------------------------------------
  // PubNub live overlay (unchanged from v1; references this.asset)
  // ---------------------------------------------------------------------------

  maybeInitPubNub() {
    if (!this.asset || this.asset.contentType !== 'live') {
      this.applyActionState('start');
      return;
    }
    const ch = (this.asset.signalChannel || '').trim();
    if (!ch) return;

    if (this.pubnub && this.pubnubReady && this.pubnub.__channel === ch) return;

    if (this.pubnub) {
      try {
        if (this.pubnubListenerRef) this.pubnub.removeListener(this.pubnubListenerRef);
        if (this.pubnub.__channel) this.pubnub.unsubscribe({ channels: [this.pubnub.__channel] });
      } catch (_) {}
    }

    this.pubnub = new PubNub({
      publishKey: 'pub-c-61be60a0-f004-4c1d-8afd-58ba45281614',
      subscribeKey: 'sub-c-15859c61-2a14-48e3-adbb-81b39c3e53e0',
      uuid: 'Volta_live'
    });
    this.pubnub.__channel = ch;
    const self = this;

    this.pubnubListenerRef = {
      message: function(mev: any) {
        const msg = mev && mev.message ? mev.message : null;
        const token = mev && mev.timetoken ? String(mev.timetoken) : '';
        const publisher = mev && mev.publisher ? String(mev.publisher) : '';

        if (publisher === self.pubnub.getUUID()) return;
        if (token && token === self.lastTokenProcessed) return;
        self.lastTokenProcessed = token;

        if (!msg || msg.type !== 'overlay') return;
        const act = (msg.action || '').toString().toLowerCase();

        let known = false;
        for (let i = 0; i < VALID_ACTIONS.length; i++) if (act === VALID_ACTIONS[i]) { known = true; break; }
        if (!known) return;

        if (act === self.lastAppliedAction && (Date.now() - self.lastAppliedAt) < 800) return;
        self.applyActionState(act as ActionT);
      },
      status: function(_st: any) {}
    };

    this.pubnub.addListener(this.pubnubListenerRef);
    this.pubnub.subscribe({ channels: [ch] });
    this.pubnubReady = true;

    this.fetchAndApplyLastAction();
  }

  fetchAndApplyLastAction() {
    if (!this.pubnub || !this.asset || !this.asset.signalChannel) {
      this.applyActionState('start');
      return;
    }
    const ch = this.asset.signalChannel;
    const self = this;
    this.pubnub.fetchMessages({ channels: [ch], count: 25 }, function(status: any, resp: any) {
      if (status && status.error) {
        console.warn('fetchMessages error', status);
        self.applyActionState('start');
        return;
      }
      const arr = resp && resp.channels && resp.channels[ch] ? resp.channels[ch] : [];
      for (let i = arr.length - 1; i >= 0; i--) {
        const itm = arr[i];
        const msg = itm && itm.message ? itm.message : null;
        if (!msg || msg.type !== 'overlay') continue;
        const act = (msg.action || '').toString().toLowerCase();
        for (let k = 0; k < VALID_ACTIONS.length; k++) {
          if (act === VALID_ACTIONS[k]) {
            self.applyActionState(act as ActionT);
            return;
          }
        }
      }
      self.applyActionState('start');
    });
  }

  applyActionState(action: string) {
    let a = (action || '').toLowerCase();
    let known = false;
    for (let i = 0; i < VALID_ACTIONS.length; i++) if (a === VALID_ACTIONS[i]) { known = true; break; }
    if (!known) a = 'start';

    this.currentOverlay = null;
    switch (a) {
      case 'start':     this.isLive = true; break;
      case 'stop':      this.isLive = false; break;
      case 'stream':
      case 'weather':
      case 'technical': this.currentOverlay = a as any; break;
      case 'clear':     break;
    }
    this.activeAction = a as any;
  }

  sendOverlayAction(action: string) {
    this.applyActionState(action);
    if (!this.pubnub || !this.isLiveContent) return;
    const channelName = this.asset.signalChannel;
    try {
      this.pubnub.signal(
        { channel: channelName, message: { action: action } },
        function(status: any, _resp: any) { if (status && status.error) console.error('signal error', status); }
      );
      this.pubnub.publish(
        { channel: channelName, message: { type: 'overlay', action: action } },
        function(status: any, _resp: any) { if (status && status.error) console.error('publish error', status); }
      );
    } catch (e) {
      console.error('PubNub send error', e);
    }
  }

  toggleDisplay(action: 'stream'|'weather'|'technical') {
    if (!this.isLiveContent) return;
    if (this.currentOverlay === action) {
      this.applyActionState('clear');
      this.sendOverlayAction('clear');
      return;
    }
    this.applyActionState(action);
    this.sendOverlayAction(action);
  }

  async startStream() {
    if (!this.isLiveContent) return;
    this.isStarting = true;
    this.applyActionState('start');
    this.sendOverlayAction('start');
    try {
      await new Promise(function(res) { setTimeout(res, 600); });
    } finally {
      this.isStarting = false;
    }
  }

  async stopStream() {
    if (!this.isLiveContent) return;
    this.isStopping = true;
    this.applyActionState('stop');
    this.sendOverlayAction('stop');
    try {
      await new Promise(function(res) { setTimeout(res, 600); });
    } finally {
      this.isStopping = false;
    }
  }

  handleRemoteOverlayChange(action: string) {
    if (action === 'start') { this.isLive = true; return; }
    if (action === 'stop')  { this.isLive = false; return; }
    if (action === 'clear') { this.currentOverlay = null; return; }
    if (action === 'stream' || action === 'technical' || action === 'weather') {
      this.currentOverlay = action as any;
    }
  }

  // ---------------------------------------------------------------------------
  // Tabs
  // ---------------------------------------------------------------------------

  setActiveTab(tab: 'visual' | 'json' | 'graph') {
    if (tab === 'json' && this.activeTab !== 'json') {
      // entering JSON tab — sync the textarea from current asset state
      this.syncRawJsonFromAsset();
    }
    this.activeTab = tab;
  }

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------

  get validationIssues(): ValidationIssue[] {
    if (!this.asset) return [];
    const live = this.svc.validateAsset(this.asset);
    if (this.rawJsonError) {
      live.unshift({
        path: '(json)',
        message: this.rawJsonError,
        severity: 'error',
        category: 'syntax'
      });
    }
    return live;
  }

  get errorCount(): number {
    return this.validationIssues.filter(i => i.severity === 'error').length;
  }

  get warningCount(): number {
    return this.validationIssues.filter(i => i.severity === 'warning').length;
  }

  /** Strict-mode (errors AND warnings both block). */
  get canSave(): boolean {
    return this.validationIssues.length === 0;
  }

  toggleValidationPanel() {
    this.showValidationPanel = !this.showValidationPanel;
  }

  /** Click an issue → navigate to the offending layout/cell. */
  goToIssue(issue: ValidationIssue) {
    // Cell-level: open the cell config popup
    const cellMatch = issue.path.match(/^layouts\[(\d+)\]\.cells\[(\d+)\]/);
    if (cellMatch) {
      const layoutIdx = parseInt(cellMatch[1], 10);
      const cellIdx = parseInt(cellMatch[2], 10);
      const layout = this.layouts[layoutIdx];
      if (layout) {
        this.setActiveTab('visual');
        setTimeout(() => {
          this.scrollToLayout(layout.id);
          setTimeout(() => this.openCellConfig(layout.id, cellIdx), 500);
        }, 50);
        return;
      }
    }

    // Layout-level: scroll to the layout card
    const layoutMatch = issue.path.match(/^layouts\[(\d+)\]/);
    if (layoutMatch) {
      const layoutIdx = parseInt(layoutMatch[1], 10);
      const layout = this.layouts[layoutIdx];
      if (layout) {
        this.setActiveTab('visual');
        setTimeout(() => this.scrollToLayout(layout.id), 50);
        return;
      }
    }

    // Stream-level: open the asset edit popup
    if (issue.path.startsWith('streamUrls')) {
      this.setActiveTab('visual');
      this.openeditAssetPopup();
      return;
    }

    // Landing-level: go to visual tab
    if (issue.path === 'defaultLayoutId') {
      this.setActiveTab('visual');
      return;
    }

    // Fallback to layoutId tag on the issue
    if (issue.layoutId) {
      this.setActiveTab('visual');
      setTimeout(() => this.scrollToLayout(issue.layoutId as string), 50);
    }
  }

  private scrollToLayout(layoutId: string) {
    const el = document.querySelector(`[data-layout-id="${layoutId}"]`) as HTMLElement | null;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('flash');
      setTimeout(() => el.classList.remove('flash'), 1400);
    }
  }

  // ---------------------------------------------------------------------------
  // Raw JSON editor
  // ---------------------------------------------------------------------------

  syncRawJsonFromAsset() {
    this.rawJsonText = JSON.stringify(this.asset, null, 2);
    this.rawJsonError = null;
    this.rawJsonDirty = false;
  }

  onRawJsonInput() {
    this.rawJsonDirty = true;
    // try parse for live feedback (no apply unless user clicks button)
    try {
      JSON.parse(this.rawJsonText);
      this.rawJsonError = null;
    } catch (e) {
      this.rawJsonError = `JSON parse error: ${(e as any).message}`;
    }
  }

  parseRawJsonAndApply() {
    let parsed: any;
    try {
      parsed = JSON.parse(this.rawJsonText);
    } catch (e) {
      this.rawJsonError = `JSON parse error: ${(e as any).message}`;
      this.toastrService.warning('JSON parse error — fix syntax before applying');
      return;
    }
    this.rawJsonError = null;
    const adapted = this.svc.adaptV1ToV2(parsed, this.assetPath);
    if (!adapted.id) adapted.id = this.assetPath;
    if (!confirm('Replace the current asset with the JSON in the editor? Unsaved visual edits will be lost.')) return;
    this.asset = adapted;
    this.tmpAsset = this.cloneAsset(this.asset);
    this.rawJsonDirty = false;
    this.updateStats();
    this.toastrService.success('JSON applied to visual editor');
  }

  copyRawJson() {
    if (!this.rawJsonText) return;
    if (navigator && (navigator as any).clipboard && (navigator as any).clipboard.writeText) {
      (navigator as any).clipboard.writeText(this.rawJsonText).then(
        () => this.toastrService.success('Copied to clipboard'),
        () => this.toastrService.warning('Copy failed')
      );
    }
  }

  /** Triggered by hidden file input. Reads .json file, populates raw editor, then applies. */
  onJsonFileSelected(event: any) {
    const file = event && event.target && event.target.files && event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const text = e.target && e.target.result;
      if (typeof text !== 'string') {
        this.toastrService.warning('Unable to read file');
        return;
      }
      // try parsing first
      try {
        JSON.parse(text);
      } catch (err) {
        this.toastrService.warning('Uploaded file is not valid JSON');
        this.rawJsonText = text;
        this.rawJsonError = `JSON parse error: ${(err as any).message}`;
        this.rawJsonDirty = true;
        this.setActiveTab('json');
        this.jsonMode = 'raw';
        return;
      }
      this.rawJsonText = text;
      this.rawJsonError = null;
      this.rawJsonDirty = true;
      this.setActiveTab('json');
      this.jsonMode = 'raw';
      // ask before replacing
      this.parseRawJsonAndApply();
      // reset the input so selecting the same file again still triggers
      event.target.value = '';
    };
    reader.onerror = () => {
      this.toastrService.warning('Failed to read file');
    };
    reader.readAsText(file);
  }

  triggerJsonUpload() {
    const el = document.getElementById('jsonUploadInput') as HTMLInputElement | null;
    if (el) el.click();
  }

  formatRawJson() {
    try {
      const parsed = JSON.parse(this.rawJsonText);
      this.rawJsonText = JSON.stringify(parsed, null, 2);
      this.rawJsonError = null;
    } catch (e) {
      this.rawJsonError = `JSON parse error: ${(e as any).message}`;
    }
  }

  // ---------------------------------------------------------------------------
  // JSON tree editor (flat list, depth-indented)
  // ---------------------------------------------------------------------------

  get treeNodes(): Array<any> {
    const nodes: any[] = [];
    this.flattenObj(this.asset, '$', '', 0, nodes);
    return nodes;
  }

  private flattenObj(obj: any, key: string, parentPath: string, depth: number, out: any[]) {
    let path: string;
    if (!parentPath) path = '';
    else if (key.startsWith('[')) path = parentPath + key;
    else path = parentPath ? parentPath + '.' + key : key;

    const type = this.getValueType(obj);
    const expandable = type === 'object' || type === 'array';
    const expanded = this.expandedTreePaths.has(path);

    out.push({
      path,
      key: key === '$' ? '(root)' : key,
      value: obj,
      type,
      depth,
      expandable,
      expanded,
      childCount: expandable ? Object.keys(obj || {}).length : 0
    });

    if (expandable && expanded) {
      if (type === 'array') {
        (obj as any[]).forEach((item: any, i: number) => {
          this.flattenObj(item, `[${i}]`, path, depth + 1, out);
        });
      } else {
        for (const k in obj) {
          if (Object.prototype.hasOwnProperty.call(obj, k)) {
            this.flattenObj(obj[k], k, path, depth + 1, out);
          }
        }
      }
    }
  }

  private getValueType(v: any): string {
    if (v === null) return 'null';
    if (v === undefined) return 'undefined';
    if (Array.isArray(v)) return 'array';
    return typeof v;
  }

  toggleTreeNode(path: string) {
    if (this.expandedTreePaths.has(path)) this.expandedTreePaths.delete(path);
    else this.expandedTreePaths.add(path);
  }

  trackByTreePath(_: number, node: any): string {
    return node.path;
  }

  editTreePrimitive(path: string, event: any) {
    const newValueStr: string = event.target.value;
    const current = this.svc.getValueAtPath(this.asset, path);
    let newValue: any = newValueStr;
    if (typeof current === 'number') {
      const n = parseFloat(newValueStr);
      newValue = isNaN(n) ? current : n;
    } else if (typeof current === 'boolean') {
      newValue = newValueStr === 'true' || newValueStr === '1';
    }
    this.svc.setValueAtPath(this.asset, path, newValue);
    this.updateStats();
  }

  editTreeBool(path: string, event: any) {
    this.svc.setValueAtPath(this.asset, path, !!event.target.checked);
    this.updateStats();
  }

  removeTreeArrayItem(path: string) {
    // path like "layouts[2]" — remove from parent array
    const m = path.match(/^(.*)\[(\d+)\]$/);
    if (!m) return;
    const parentPath = m[1];
    const idx = parseInt(m[2], 10);
    const arr = this.svc.getValueAtPath(this.asset, parentPath);
    if (!Array.isArray(arr)) return;
    if (!confirm('Remove this item from the array?')) return;
    arr.splice(idx, 1);
    this.updateStats();
  }

  // ---------------------------------------------------------------------------
  // Graph view
  // ---------------------------------------------------------------------------

  graphCanvasWidth = 1100;

  get graphData(): { nodes: any[]; edges: any[]; height: number } {
    const layouts = this.layouts;
    if (!layouts.length) return { nodes: [], edges: [], height: 200 };

    // BFS from landing (or first enabled) to compute depths
    const startId = this.asset.defaultLayoutId || (layouts[0] && layouts[0].id) || null;
    const depths: { [k: string]: number } = {};
    if (startId) {
      const queue: Array<{ id: string; d: number }> = [{ id: startId, d: 0 }];
      while (queue.length) {
        const item = queue.shift() as { id: string; d: number };
        if (depths[item.id] !== undefined) continue;
        depths[item.id] = item.d;
        const l = layouts.find(x => x.id === item.id);
        if (!l) continue;
        for (const c of l.cells) {
          if (c.type === 'multiviewLink') {
            queue.push({ id: (c as MultiviewLinkCell).targetLayoutId, d: item.d + 1 });
          }
        }
      }
    }

    const orphans: Layout[] = [];
    const byDepth: { [d: number]: Layout[] } = {};
    const incomingIds = new Set<string>();
    const outgoingIds = new Set<string>();
    for (const l of layouts) {
      for (const c of l.cells) {
        if (c.type === 'multiviewLink') {
          outgoingIds.add(l.id);
          incomingIds.add((c as MultiviewLinkCell).targetLayoutId);
        }
      }
    }
    for (const l of layouts) {
      if (depths[l.id] !== undefined) {
        const d = depths[l.id];
        if (!byDepth[d]) byDepth[d] = [];
        byDepth[d].push(l);
      } else {
        orphans.push(l);
      }
    }

    const nodeW = 170;
    const nodeH = 90;
    const gapX = 30;
    const gapY = 70;

    const nodes: any[] = [];
    const depthKeys = Object.keys(byDepth).map(d => parseInt(d, 10)).sort((a, b) => a - b);
    let y = 40;
    for (const d of depthKeys) {
      const row = byDepth[d];
      const totalW = row.length * nodeW + (row.length - 1) * gapX;
      let x = (this.graphCanvasWidth - totalW) / 2;
      for (const l of row) {
        nodes.push({
          id: l.id,
          name: l.name || '(unnamed)',
          x, y, w: nodeW, h: nodeH,
          isLanding: l.id === this.asset.defaultLayoutId,
          isOrphan: false,
          enabled: l.enabled,
          hasIncoming: incomingIds.has(l.id),
          hasOutgoing: outgoingIds.has(l.id),
          cellCount: l.cells.length,
          masterUrl: l.masterUrl
        });
        x += nodeW + gapX;
      }
      y += nodeH + gapY;
    }
    if (orphans.length) {
      y += gapY / 2;
      const totalW = orphans.length * nodeW + (orphans.length - 1) * gapX;
      let x = (this.graphCanvasWidth - totalW) / 2;
      for (const l of orphans) {
        nodes.push({
          id: l.id,
          name: l.name || '(unnamed)',
          x, y, w: nodeW, h: nodeH,
          isLanding: false,
          isOrphan: true,
          enabled: l.enabled,
          hasIncoming: incomingIds.has(l.id),
          hasOutgoing: outgoingIds.has(l.id),
          cellCount: l.cells.length,
          masterUrl: l.masterUrl
        });
        x += nodeW + gapX;
      }
      y += nodeH;
    }

    // edges
    const nodeMap = new Map<string, any>();
    for (const n of nodes) nodeMap.set(n.id, n);
    const edges: any[] = [];
    for (const l of layouts) {
      const src = nodeMap.get(l.id);
      if (!src) continue;
      for (const c of l.cells) {
        if (c.type === 'multiviewLink') {
          const tgt = nodeMap.get((c as MultiviewLinkCell).targetLayoutId);
          if (!tgt) continue;
          edges.push({
            fromId: src.id,
            toId: tgt.id,
            fromX: src.x + src.w / 2,
            fromY: src.y + src.h,
            toX: tgt.x + tgt.w / 2,
            toY: tgt.y
          });
        }
      }
    }

    return { nodes, edges, height: y + 40 };
  }

  edgePath(e: any): string {
    // Curved path from source bottom to target top
    const dy = e.toY - e.fromY;
    const c1x = e.fromX;
    const c1y = e.fromY + dy * 0.45;
    const c2x = e.toX;
    const c2y = e.toY - dy * 0.45;
    return `M ${e.fromX} ${e.fromY} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${e.toX} ${e.toY}`;
  }

  jumpToLayout(layoutId: string) {
    this.setActiveTab('visual');
    setTimeout(() => this.scrollToLayout(layoutId), 50);
  }

  // ---------------------------------------------------------------------------
  // Global key handler
  // ---------------------------------------------------------------------------

  @HostListener('document:keydown', ['$event'])
  onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      if (this.showCellPopup) this.closeCellConfig();
      if (this.showAssetEditPopup) this.closeAssetEditPopup();
      if (this.showAddLayoutPopup) this.closeAddLayoutPopup();
    }
  }
}
